import { MouseEvent, ReactNode } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { Button, Chip, TooltipText } from 'components';
import { ColorContextProvider } from './ColorContext';
import { Colors } from '@app/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.borderRadius.std,
    border: {
      style: 'solid',
      width: 1,
      color: theme.colors.grayBorder,
    },
    noMargin: {
      marginRight: 0,
    },
    cursor: 'pointer',

    transition: 'box-shadow .3s',
    backgroundColor: theme.colors.white,

    [theme.media.mobile]: {
      marginBottom: theme.marginBase * 2,
    },

    '&:hover': {
      boxShadow: theme.boxShadow.base,
    },
  },
  mostPopularContainer: {
    borderColor: ({ color }: { color: Colors }) =>
      theme.colorDefinitions[color].std,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  containerSelected: {
    boxShadow: theme.boxShadow.base,
    cursor: 'auto',
  },
  containerDisabled: {
    cursor: 'auto',
    opacity: 0.4,
  },
  mostPopular: {
    ...theme.fonts.label,
    position: 'absolute',
    zIndex: -1,
    top: -theme.marginBase * 4,
    paddingTop: theme.marginBase / 2,
    left: -1,
    right: -1,
    height: 100,
    borderRadius: [[theme.borderRadius.std, theme.borderRadius.std, 0, 0]],
    backgroundColor: ({ color }: { color: Colors }) =>
      theme.colorDefinitions[color].accessible,
    textAlign: 'center',
    color: 'white',
  },
  icon: {
    color: theme.colors.green,
    position: 'absolute',
    top: theme.marginBase * 2,
    left: theme.marginBase * 2,
    fontSize: theme.iconSize.large,
  },
  header: {
    position: 'relative',
    backgroundColor: theme.colors.grayBackground,
    overflow: 'hidden',
    padding: theme.marginBase * 3,
    transition: 'background-color .3s',
    minHeight: 130,

    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  headerSelected: {
    backgroundColor: ({ color }: { color: Colors }) =>
      theme.colorDefinitions[color].muted,
  },
  chipContainer: {
    textTransform: 'uppercase',
  },
  description: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
    padding: theme.marginBase * 2,
  },
  buttonContainer: {
    margin: theme.marginBase * 2,
  },
  button: {
    width: '100%',
  },
  buttonIcon: {
    marginRight: theme.marginBase,
  },
}));

export interface OfferBlockProps {
  offerName: string;
  priceBlock: ReactNode;
  description?: string;
  color: Colors;
  children?: ReactNode;
  buttonLabel?: string;
  selected?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => any;
  disabled?: boolean;
  tooltip?: string;
  selectMode?: boolean;
  mostPopular?: boolean;
}

export const OfferBlockDisplay = ({
  priceBlock,
  offerName,
  description,
  color,
  selected,
  children,
  onClick,
  disabled,
  tooltip,
  buttonLabel,
  selectMode,
  mostPopular,
}: OfferBlockProps) => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const classes = useStyles({ theme, color });

  return (
    <TooltipText title={tooltip}>
      <div
        className={classnames(classes.container, {
          [classes.containerSelected]: !!selected,
          [classes.containerDisabled]: !!disabled,
          [classes.mostPopularContainer]: !!mostPopular,
        })}
        onClick={disabled ? undefined : onClick}
      >
        {mostPopular && (
          <div className={classes.mostPopular}>
            {t('billing.offers.block.mostPopular')}
          </div>
        )}
        <div className={classes.mainContent}>
          {selected && (
            <i
              className={classnames('ri-checkbox-circle-fill', classes.icon)}
            />
          )}

          <div
            className={classnames(classes.header, {
              [classes.headerSelected]: !!selected,
            })}
          >
            <div className={classes.chipContainer}>
              <Chip color={color} variant={selected ? 'inverted' : 'default'}>
                {offerName}
              </Chip>
            </div>

            {priceBlock}

            {description && (
              <div className={classes.description}>{description}</div>
            )}
          </div>
          {!!children && (
            <ColorContextProvider value={color}>
              <ul className={classes.content}>{children}</ul>
            </ColorContextProvider>
          )}
        </div>
        {buttonLabel && (
          <div className={classes.buttonContainer}>
            <Button
              text={buttonLabel}
              color={color}
              small
              variant={
                selectMode
                  ? selected
                    ? 'plain'
                    : 'muted'
                  : selected
                  ? 'muted'
                  : 'plain'
              }
              className={classes.button}
            >
              {selected && (
                <i
                  className={classnames('ri-check-line', classes.buttonIcon)}
                />
              )}
            </Button>
          </div>
        )}
      </div>
    </TooltipText>
  );
};
